// iter579 — Suppliers reconciliation health widget.
//
// Single-glance month-end confidence indicator. Computes the same
// gap that the Suppliers page Worst-Offenders banner uses
// (|Outstanding − current_credit| ≥ SR 1, where
//  Outstanding = Total − Cash − Bank − Returns).
//
// Green tile when gap-list is empty (the common case after iter578).
// Amber tile with the top 3 offenders when something needs attention.
// Click anywhere → /suppliers (the Worst-Offenders banner takes over there).
//
// iter580 — read-only by design. No "auto-fix" or destructive action
// lives on this card; a misfire on a fix-data action could corrupt every
// supplier's current_credit. The card only NAVIGATES — actual fixes
// happen on the Suppliers page where the user has full context.
//
// iter580 — 5-min in-memory cache. Data only changes on supplier
// mutations (bills / payments / returns), so repeated dashboard mounts in
// the same session reuse the previous fetch. Cache is module-level so it
// survives unmount/remount of the widget (e.g. when navigating away and
// back). Call invalidateSupplierReconHealthCache() from any mutation site
// to force a refresh.
import { cur } from '@/lib/currency';
import { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { CheckCircle2, AlertTriangle, ChevronRight, Loader2, HelpCircle } from 'lucide-react';
import api from '@/lib/api';
import { useNavigate } from 'react-router-dom';

const CACHE_TTL_MS = 5 * 60 * 1000;
let _cache = null;
const _readCache = () => {
  if (!_cache) return null;
  if (Date.now() - _cache.ts > CACHE_TTL_MS) { _cache = null; return null; }
  return _cache.payload;
};
const _writeCache = (payload) => { _cache = { ts: Date.now(), payload }; };
export const invalidateSupplierReconHealthCache = () => { _cache = null; };

export default function SupplierReconciliationHealthWidget() {
  const cached = _readCache();
  const [state, setState] = useState(
    cached
      ? { loading: false, ...cached }
      : { loading: true, offenders: null, supplierCount: 0 }
  );
  const navigate = useNavigate();

  useEffect(() => {
    if (cached) return;
    let cancelled = false;
    (async () => {
      try {
        const [sR, psR] = await Promise.all([
          api.get('/suppliers'),
          api.get('/suppliers/payment-summaries'),
        ]);
        if (cancelled) return;
        const summaries = psR.data || {};
        const offenders = (sR.data || [])
          .map((s) => {
            const ps = summaries[s.id] || {};
            const total = Number(s.total_purchases || 0);
            const cash = Number(ps.cash || 0);
            const bank = Number(ps.bank || 0);
            const returns = Number(ps.returns || 0);
            const outstanding = Math.max(0, total - cash - bank - returns);
            const credit = Number(s.current_credit || 0);
            const gap = Math.abs(outstanding - credit);
            return { id: s.id, name: s.name, outstanding, credit, gap };
          })
          .filter((x) => x.gap >= 1)
          .sort((a, b) => b.gap - a.gap);
        const payload = { offenders, supplierCount: (sR.data || []).length };
        _writeCache(payload);
        setState({ loading: false, ...payload });
      } catch {
        if (!cancelled) setState({ loading: false, offenders: null, supplierCount: 0 });
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (state.loading) {
    return (
      <Card className="border-border bg-muted/20" data-testid="supplier-recon-health-widget-loading">
        <CardContent className="pt-4 pb-4 flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 size={14} className="animate-spin" /> Checking supplier reconciliation health…
        </CardContent>
      </Card>
    );
  }
  if (state.offenders === null) return null;
  if (state.supplierCount === 0) return null;

  const clean = state.offenders.length === 0;
  const fmt0 = (n) => `${cur()} ${Math.round(Number(n) || 0).toLocaleString()}`;
  const tooltipBody = clean
    ? `Read-only health check. Compares every supplier's Outstanding (Total − Cash − Bank − Returns) against their Current Credit Status. All ${state.supplierCount} suppliers currently reconcile · click to open the Suppliers page.`
    : `Read-only health check. ${state.offenders.length} supplier${state.offenders.length === 1 ? '' : 's'} have a gap between Outstanding and Current Credit Status. Click to open the Suppliers page where you can investigate each one manually — no auto-fix runs from here.`;

  return (
    <TooltipProvider delayDuration={200}>
      <Card
        className={`cursor-pointer transition-colors ${clean
          ? 'border-emerald-200 bg-emerald-50/40 hover:bg-emerald-50/70 dark:border-emerald-900 dark:bg-emerald-950/20'
          : 'border-amber-300 bg-amber-50/50 hover:bg-amber-50/80 dark:border-amber-900 dark:bg-amber-950/20'}`}
        onClick={() => navigate('/suppliers')}
        data-testid="supplier-recon-health-widget"
      >
        <CardContent className="pt-4 pb-4 space-y-2 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${clean
                ? 'bg-emerald-100 dark:bg-emerald-900/40'
                : 'bg-amber-100 dark:bg-amber-900/40'}`}
              >
                {clean
                  ? <CheckCircle2 size={14} className="text-emerald-700 dark:text-emerald-400" />
                  : <AlertTriangle size={14} className="text-amber-700 dark:text-amber-400" />}
              </div>
              <div className="min-w-0">
                <h3 className="text-sm font-semibold font-outfit inline-flex items-center gap-1.5" data-testid="supplier-recon-health-title">
                  Suppliers needing reconciliation
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        onClick={(e) => e.stopPropagation()}
                        className="text-muted-foreground hover:text-foreground transition-colors"
                        data-testid="supplier-recon-health-help"
                        aria-label="What does this widget show?"
                      >
                        <HelpCircle size={12} />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" align="start" className="max-w-xs text-xs leading-relaxed">
                      <p className="font-semibold mb-1">Read-only month-end health check</p>
                      <p className="mb-1.5">
                        Compares each supplier&apos;s <b>Outstanding</b> (Total Purchases − Cash Paid − Bank Paid − Returns) against their stored <b>Credit Status</b>. A gap means a real data-integrity issue worth investigating.
                      </p>
                      <p className="text-amber-700 dark:text-amber-300">
                        No auto-fix runs from this card — clicking only navigates to the Suppliers page. Use the Reconcile dialog on each supplier card to fix issues manually.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </h3>
                <p className="text-[10px] text-muted-foreground">
                  {clean
                    ? `All ${state.supplierCount} suppliers reconcile · Outstanding = Credit Status`
                    : 'Outstanding ≠ Current Credit for the suppliers below'}
                </p>
              </div>
            </div>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center gap-1 shrink-0">
                  <span
                    className={`text-lg font-bold tabular-nums ${clean
                      ? 'text-emerald-700 dark:text-emerald-300'
                      : 'text-amber-800 dark:text-amber-300'}`}
                    data-testid="supplier-recon-health-count"
                  >
                    {clean ? '0' : state.offenders.length}
                  </span>
                  {clean && <span className="text-emerald-700 dark:text-emerald-300 text-lg font-bold">✓</span>}
                  <ChevronRight size={14} className="text-muted-foreground" />
                </div>
              </TooltipTrigger>
              <TooltipContent side="left" className="max-w-xs text-xs">
                {tooltipBody}
              </TooltipContent>
            </Tooltip>
          </div>

          {!clean && (
            <div className="space-y-1 pt-1 border-t border-amber-200/60 dark:border-amber-800/40" data-testid="supplier-recon-health-list">
              {state.offenders.slice(0, 3).map((o) => (
                <div key={o.id} className="flex items-center justify-between text-[11px] gap-2">
                  <span className="truncate font-medium text-foreground" title={o.name}>{o.name}</span>
                  <span className="text-rose-700 dark:text-rose-300 font-semibold tabular-nums shrink-0">Δ {fmt0(o.gap)}</span>
                </div>
              ))}
              {state.offenders.length > 3 && (
                <div className="text-[10px] text-muted-foreground pt-0.5">
                  + {state.offenders.length - 3} more · open Suppliers page to see all
                </div>
              )}
              <Button
                size="sm"
                variant="outline"
                className="w-full h-7 text-[11px] mt-1 border-amber-400 text-amber-800 hover:bg-amber-100 dark:hover:bg-amber-900/40"
                data-testid="supplier-recon-health-cta"
                onClick={(e) => { e.stopPropagation(); navigate('/suppliers'); }}
              >
                Open Worst-Offenders queue →
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </TooltipProvider>
  );
}
